// Pre-create check (atomic-design Rule 3): considered `TemplatePreview` — rejected because it
// previews a SCHEMA (it takes `schema` + `matchedUseCases` and renders a `PropertyList` with
// its own close button). This previews a `SpaceDocument` by mounting it in a real
// `CompositionHost`, so the card shows the composition as it will actually render rather than
// a description of it. Different input, different mechanism, no shared surface to extend.
import React, { useEffect, useMemo, useRef } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { SpaceDocument } from 'types/space';
import CompositionHost from '../../../composition-host/CompositionHost';
import { useCompositionResolver } from '../../../composition-host/useCompositionResolver';
import type { SpaceHostMessage } from '../../../composition-host/types';
import './GalleryCardPreviewMolecule.scss';

export interface GalleryCardPreviewMoleculeProps {
  document: SpaceDocument;
  apiClient?: ApiClient;
  surfaceId: string;
}

/**
 * A gallery card's live mini-render (SC1) — the same `CompositionHost` + composition-resolver
 * pipeline every other preview surface in the composer uses (`RegistryExplorerOrganism`, the
 * canvas), so a card shows exactly what opening it would render rather than a hand-maintained
 * thumbnail. The card itself is the click target (`ComposerGalleryOrganism` renders this inside a
 * `div[role="button"]`, never a real `<button>`, precisely because a resolved composition can
 * contain real interactive elements — nesting those inside a native button would be invalid HTML).
 * `inert` is set imperatively via ref because the installed `@types/react` has no `inert` prop yet;
 * it strips both pointer AND keyboard reachability from every descendant, which `aria-hidden` alone
 * does not guarantee for focusable elements.
 */
const GalleryCardPreviewMolecule: React.FC<GalleryCardPreviewMoleculeProps> = ({ document, apiClient, surfaceId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { resolveComposition, isResolving } = useCompositionResolver(document, apiClient);

  useEffect(() => {
    const node = containerRef.current;
    if (node) (node as unknown as { inert: boolean }).inert = true;
  }, []);

  const messages: SpaceHostMessage[] = useMemo(
    () => [
      {
        type: 'createSurface',
        surfaceId,
        components: document.a2ui.components,
        root: document.a2ui.root,
      },
    ],
    [document, surfaceId]
  );

  return (
    <div
      ref={containerRef}
      className="gallery-card-preview"
      data-testid="gallery-card-preview"
      aria-hidden="true"
      tabIndex={-1}
    >
      {!isResolving && (
        <CompositionHost messages={messages} surfaceId={surfaceId} mode="preview" resolveComposition={resolveComposition} />
      )}
    </div>
  );
};

export default GalleryCardPreviewMolecule;
